import { observer } from 'mobx-react-lite'
import { type StateAmountEntryViewModel } from '@/features/jobs/details/components/FinalReturnAmountsDialog/StateAmountEntryViewModel'
import { type YearAmountEntryViewModel } from '@/features/jobs/details/components/FinalReturnAmountsDialog/YearAmountEntryViewModel'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { US_STATES } from '@/assets/data/us-states-territories'
import {
  FinalReturnAmountsDialog,
  FinalReturnAmountsStateEntry,
  FinalReturnAmountsYearSection,
} from '@/features/jobs/details/components/FinalReturnAmountsDialog'
import { type FinalReturnAmountsDialogViewModel } from './FinalReturnAmountsDialogViewModel'

export interface FinalReturnAmountsDialogContainerProps {
  vm: FinalReturnAmountsDialogViewModel
  submitLabel?: string
}

export const FinalReturnAmountsDialogContainer = observer(
  function FinalReturnAmountsDialogContainer({
    vm,
    submitLabel,
  }: FinalReturnAmountsDialogContainerProps) {
    return (
      <FinalReturnAmountsDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        onAddYear={vm.addYearEntry}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
        isSubmitDisabled={vm.submit.pending || vm.hasIncompleteEntries}
        submitLabel={submitLabel}
      >
        {vm.yearEntries.map((entry, index) => (
          <TaxYearSectionContainer
            key={entry.id}
            entryVm={entry}
            index={index}
            yearOptions={vm.taxYearOptions}
            onRemove={() => vm.removeYearEntry(index)}
          />
        ))}
      </FinalReturnAmountsDialog>
    )
  },
)

interface TaxYearSectionContainerProps {
  entryVm: YearAmountEntryViewModel
  index: number
  yearOptions: [string, string][]
  onRemove: () => void
}

const TaxYearSectionContainer = observer(function TaxYearSectionContainer({
  entryVm,
  index,
  yearOptions,
  onRemove,
}: TaxYearSectionContainerProps) {
  return (
    <FinalReturnAmountsYearSection
      index={index}
      taxYearInput={
        <SelectContainer
          model={entryVm}
          field="taxYear"
          items={yearOptions}
          label="Tax year"
          placeholder="Select a tax year"
          data-test={`final-return-amounts-tax-year-${index}`}
        />
      }
      federalAmountInput={
        <NumericInputContainer
          model={entryVm}
          precision={2}
          field="federalAmount"
          name={`final-return-amounts-federal-amount-${index}`}
          label="Federal amount"
          testId={`final-return-amounts-federal-amount-${index}`}
        />
      }
      onRemove={onRemove}
      onAddState={entryVm.addStateEntry}
    >
      {entryVm.stateEntries.map((stateEntry, stateIndex) => (
        <StateEntryItemContainer
          key={stateEntry.id}
          entryVm={stateEntry}
          yearIndex={index}
          index={stateIndex}
          onRemove={() => entryVm.removeStateEntry(stateIndex)}
        />
      ))}
    </FinalReturnAmountsYearSection>
  )
})

interface StateEntryItemContainerProps {
  entryVm: StateAmountEntryViewModel
  yearIndex: number
  index: number
  onRemove: () => void
}

const StateEntryItemContainer = observer(function StateEntryItemContainer({
  entryVm,
  yearIndex,
  index,
  onRemove,
}: StateEntryItemContainerProps) {
  return (
    <FinalReturnAmountsStateEntry
      yearIndex={yearIndex}
      index={index}
      stateInput={
        <SelectContainer
          model={entryVm}
          field="state"
          items={US_STATES}
          label="State"
          placeholder="Select a state"
          data-test={`final-return-amounts-state-name-${yearIndex}-${index}`}
        />
      }
      amountInput={
        <NumericInputContainer
          model={entryVm}
          precision={2}
          field="amount"
          name={`final-return-amounts-state-amount-${yearIndex}-${index}`}
          label="Amount"
          testId={`final-return-amounts-state-amount-${yearIndex}-${index}`}
        />
      }
      onRemove={onRemove}
    />
  )
})
